import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

export default function Contact() {
  const [site, setSite] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => { api.get("/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We will get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const s = site || {};

  return (
    <div>
      <section className="hero-mesh grain">
        <div className="container-wide py-20 md:py-28">
          <div className="overline">Contact</div>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">Let's talk about your project.</h1>
          <p className="mt-6 max-w-2xl text-[color:var(--sky-muted)] text-lg leading-relaxed">
            Reach out for consulting engagements, staffing briefs, product demos, or general questions.
            We reply within 1 business day.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-wide grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="card-flat p-6">
              <div className="overline">Direct</div>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail size={16} className="text-[color:var(--sky-navy)] mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--sky-muted)]">Email</div>
                    <a href={`mailto:${s.company_email || 'info@skyverraconsulting.net'}`} className="font-medium break-all">{s.company_email || 'info@skyverraconsulting.net'}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="text-[color:var(--sky-navy)] mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--sky-muted)]">Phone</div>
                    <a href={`tel:${s.company_phone || '8511236347'}`} className="font-medium">{s.company_phone || '8511236347'}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-[color:var(--sky-navy)] mt-1" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[color:var(--sky-muted)]">Location</div>
                    <div className="font-medium">{s.company_address || 'India'}</div>
                  </div>
                </li>
              </ul>
              <div className="mt-8 pt-6 border-t border-[color:var(--sky-border)]">
                <div className="overline">Hours</div>
                <p className="mt-2 text-sm">Mon – Fri · 09:00 – 19:00 IST</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <form data-testid={CONTACT.form} onSubmit={submit} className="card-flat p-6 md:p-8 grid gap-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div className="grid gap-1">
                  <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Full name</label>
                  <input required data-testid={CONTACT.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Email</label>
                  <input required type="email" data-testid={CONTACT.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="grid gap-1">
                  <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Phone</label>
                  <input data-testid={CONTACT.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
                </div>
                <div className="grid gap-1">
                  <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Company</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
                </div>
              </div>
              <div className="grid gap-1">
                <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Subject</label>
                <input data-testid={CONTACT.subject} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
              </div>
              <div className="grid gap-1">
                <label className="text-xs uppercase tracking-widest font-semibold text-[color:var(--sky-muted)]">Message</label>
                <textarea required rows={5} data-testid={CONTACT.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="border border-[color:var(--sky-border)] px-3 py-2 rounded-sm ring-focus" />
              </div>
              <button disabled={submitting} data-testid={CONTACT.submit} className="btn-primary self-start mt-2 disabled:opacity-60">
                {submitting ? "Sending…" : "Send message"} <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
